import { useMemo } from 'react'
import {
  PiArrowBendUpLeft,
  PiArrowBendDoubleUpLeft,
  PiArrowBendUpRight,
  PiBell,
  PiChartLineUp,
  PiPath,
  PiTrash,
} from 'react-icons/pi'
import { ComposeMode } from './ComposePanel'

export interface EmailAction {
  label: string
  icon: React.ComponentType<{ className?: string; size?: number }>
  onClick: () => void
  disabled: boolean
  hasSeparatorAfter?: boolean
  /** When provided, renders as a nested submenu instead of a regular menu item. */
  subContent?: React.ReactNode
}

/** Shared callback props for email deal actions, used by action bar, message, and menu. */
export interface EmailDealActionCallbacks {
  dealId?: number | null
  onAssignToDeal?: () => void
  onRemoveFromDeal?: () => void
  onCreateDeal?: () => void
  onUpdateSalesStage?: () => void
  onScheduleReminder?: () => void
}

interface EmailActionContext extends EmailDealActionCallbacks {
  onCompose?: (mode: ComposeMode) => void
  dealId: number | null
  /** React element for the deal search submenu content. */
  assignDealSubContent?: React.ReactNode
}

/** Create thread-level actions, varying based on deal assignment state. */
function createEmailThreadActions(ctx: EmailActionContext): EmailAction[] {
  const composeDisabled = !ctx.onCompose
  const actions: EmailAction[] = [
    {
      label: 'Reply',
      icon: PiArrowBendUpLeft,
      onClick: () => ctx.onCompose?.('reply'),
      disabled: composeDisabled,
    },
    {
      label: 'Reply All',
      icon: PiArrowBendDoubleUpLeft,
      onClick: () => ctx.onCompose?.('reply_all'),
      disabled: composeDisabled,
    },
    {
      label: 'Forward',
      icon: PiArrowBendUpRight,
      onClick: () => ctx.onCompose?.('forward'),
      disabled: composeDisabled,
      hasSeparatorAfter: true,
    },
    {
      label: 'Schedule a reminder',
      icon: PiBell,
      onClick: () => ctx.onScheduleReminder?.(),
      disabled: !ctx.onScheduleReminder,
    },
  ]

  if (ctx.dealId) {
    // Thread has a deal assigned
    actions.push(
      {
        label: 'Update sales stage',
        icon: PiChartLineUp,
        onClick: () => ctx.onUpdateSalesStage?.(),
        disabled: !ctx.onUpdateSalesStage,
      },
      {
        label: 'Remove from deal',
        icon: PiTrash,
        onClick: () => ctx.onRemoveFromDeal?.(),
        disabled: !ctx.onRemoveFromDeal,
      }
    )
  } else {
    // Thread has no deal assigned
    actions.push(
      {
        label: 'Create a deal',
        icon: PiChartLineUp,
        onClick: () => ctx.onCreateDeal?.(),
        disabled: !ctx.onCreateDeal,
      },
      {
        label: 'Assign to deal',
        icon: PiPath,
        onClick: () => ctx.onAssignToDeal?.(),
        disabled: !ctx.onAssignToDeal,
        subContent: ctx.assignDealSubContent,
      }
    )
  }

  return actions
}

/** Build an EmailActionContext from deal callbacks and optional compose handler. */
export function buildActionContext(
  dealCallbacks: EmailDealActionCallbacks,
  onCompose?: (mode: ComposeMode) => void,
  assignDealSubContent?: React.ReactNode
): EmailActionContext {
  const { dealId, ...rest } = dealCallbacks
  return {
    ...rest,
    onCompose,
    dealId: dealId ?? null,
    assignDealSubContent,
  }
}

/** Memoized email thread actions based on deal context. */
export function useEmailThreadActions(ctx: EmailActionContext): EmailAction[] {
  return useMemo(
    () => createEmailThreadActions(ctx),
    [
      ctx.onCompose,
      ctx.dealId,
      ctx.onAssignToDeal,
      ctx.onRemoveFromDeal,
      ctx.onCreateDeal,
      ctx.onUpdateSalesStage,
      ctx.onScheduleReminder,
      ctx.assignDealSubContent,
    ]
  )
}
